/**
 * Vario passes the app token to our frontend in three possible ways, tried in order:
 *
 *  1. URL query params  - `?appToken=…&appIdentifier=…&offlineToken=…`
 *     This is what Vario actually does in both Tenant-iframe and admin.vario.cloud Install-flow.
 *  2. URL fragment      - `#token=…`
 *     Dev-only shortcut for the Vite dev server (avoids referer leakage).
 *  3. window.postMessage from the parent - `{ type: 'vario:hello', appToken }`
 *     Theoretical fallback if Vario ever switches to the postMessage pattern.
 *
 * `offlineToken` (presence ⇒ install/refresh flow) and `appIdentifier` are returned alongside
 * the appToken so the caller can branch (provisioning vs. plain session exchange).
 */
export interface VarioHandshake {
  appToken: string;
  appIdentifier?: string;
  offlineToken?: string;
}

export function waitForHandshake(allowedOrigins: string[], timeoutMs = 15_000): Promise<VarioHandshake> {
  // 1. URL query params - the actual Vario behaviour.
  const queryHandshake = readQueryHandshake();
  if (queryHandshake) return Promise.resolve(queryHandshake);

  // 2. Fragment-based dev mode shortcut.
  const fragmentToken = readFragmentToken();
  if (fragmentToken) return Promise.resolve({ appToken: fragmentToken });

  // 3. postMessage fallback.
  return new Promise<VarioHandshake>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('Vario-Handshake timeout - weder ?appToken in URL noch postMessage vom Slot-Host'));
    }, timeoutMs);

    const onMessage = (event: MessageEvent) => {
      if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) return;
      const data = event.data as { type?: string; appToken?: string } | null;
      if (!data || data.type !== 'vario:hello' || typeof data.appToken !== 'string') return;
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve({ appToken: data.appToken });
    };

    window.addEventListener('message', onMessage);

    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({ type: 'vario:ready' }, '*');
      } catch {
        // ignore
      }
    }
  });
}

function readQueryHandshake(): VarioHandshake | null {
  const search = new URLSearchParams(window.location.search);
  const appToken = search.get('appToken');
  if (!appToken) return null;
  const result: VarioHandshake = { appToken };
  const appIdentifier = search.get('appIdentifier');
  if (appIdentifier) result.appIdentifier = appIdentifier;
  const offlineToken = search.get('offlineToken');
  if (offlineToken) result.offlineToken = offlineToken;
  return result;
}

function readFragmentToken(): string | null {
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  return params.get('token');
}
